const IDENTITY_CROP = { x: 0, y: 0, width: 1, height: 1 };
const CROP_EPSILON = 1e-4;

const CANONICAL_CORNER = {
  n: "n",
  s: "s",
  e: "e",
  w: "w",
  nw: "nw",
  ne: "ne",
  sw: "sw",
  se: "se",
  top: "n",
  bottom: "s",
  left: "w",
  right: "e",
  topLeft: "nw",
  topRight: "ne",
  bottomLeft: "sw",
  bottomRight: "se",
};

const clamp = ({ value, min, max }) => Math.min(max, Math.max(min, value));

export const normalizeCrop = (crop) => {
  if (!crop || typeof crop !== "object") return { ...IDENTITY_CROP };
  const width = clamp({
    value: Number(crop.width) || 0,
    min: CROP_EPSILON,
    max: 1,
  });
  const height = clamp({
    value: Number(crop.height) || 0,
    min: CROP_EPSILON,
    max: 1,
  });
  return {
    x: clamp({ value: Number(crop.x) || 0, min: 0, max: 1 - width }),
    y: clamp({ value: Number(crop.y) || 0, min: 0, max: 1 - height }),
    width,
    height,
  };
};

export const isIdentityCrop = (crop) => {
  if (!crop) return true;
  const next = normalizeCrop(crop);
  return (
    next.x <= CROP_EPSILON &&
    next.y <= CROP_EPSILON &&
    next.width >= 1 - CROP_EPSILON &&
    next.height >= 1 - CROP_EPSILON
  );
};

export const persistableCrop = (crop) =>
  isIdentityCrop(crop) ? undefined : normalizeCrop(crop);

export const cropsEqual = (left, right) => {
  if (isIdentityCrop(left) && isIdentityCrop(right)) return true;
  if (isIdentityCrop(left) || isIdentityCrop(right)) return false;
  const first = normalizeCrop(left);
  const second = normalizeCrop(right);
  return (
    Math.abs(first.x - second.x) < 0.001 &&
    Math.abs(first.y - second.y) < 0.001 &&
    Math.abs(first.width - second.width) < 0.001 &&
    Math.abs(first.height - second.height) < 0.001
  );
};

export const canCropImageLayer = ({ type, isBackground } = {}) =>
  type === "image" && !isBackground;

export const contentBoxFromFrame = ({ box, crop } = {}) => {
  const frame = {
    x: Number(box?.x) || 0,
    y: Number(box?.y) || 0,
    width: Math.max(0, Number(box?.width) || 0),
    height: Math.max(0, Number(box?.height) || 0),
  };
  if (isIdentityCrop(crop) || frame.width <= 0 || frame.height <= 0) {
    return frame;
  }
  const next = normalizeCrop(crop);
  const width = frame.width / next.width;
  const height = frame.height / next.height;
  return {
    x: frame.x - next.x * width,
    y: frame.y - next.y * height,
    width,
    height,
  };
};

export const cropFromFrameInContent = ({ frame, content }) => {
  const contentW = Math.max(Number(content?.width) || 0, CROP_EPSILON);
  const contentH = Math.max(Number(content?.height) || 0, CROP_EPSILON);
  return persistableCrop({
    x: ((Number(frame?.x) || 0) - (Number(content?.x) || 0)) / contentW,
    y: ((Number(frame?.y) || 0) - (Number(content?.y) || 0)) / contentH,
    width: (Number(frame?.width) || 0) / contentW,
    height: (Number(frame?.height) || 0) / contentH,
  });
};

const resizeFrameIndependent = ({
  origin,
  handle,
  startPoint,
  point,
  minSize,
}) => {
  const dx = point.x - startPoint.x;
  const dy = point.y - startPoint.y;
  const originRight = origin.x + origin.width;
  const originBottom = origin.y + origin.height;
  let x = origin.x;
  let y = origin.y;
  let width = origin.width;
  let height = origin.height;

  if (handle.includes("w")) {
    x = Math.min(origin.x + dx, originRight - minSize);
    width = originRight - x;
  } else if (handle.includes("e")) {
    width = Math.max(minSize, origin.width + dx);
  }

  if (handle.includes("n")) {
    y = Math.min(origin.y + dy, originBottom - minSize);
    height = originBottom - y;
  } else if (handle.includes("s")) {
    height = Math.max(minSize, origin.height + dy);
  }

  return { x, y, width, height };
};

const clampFrameToContent = ({
  frame,
  content,
  originFrame,
  handle,
  minSize,
}) => {
  const contentRight = content.x + content.width;
  const contentBottom = content.y + content.height;
  const originRight = originFrame.x + originFrame.width;
  const originBottom = originFrame.y + originFrame.height;
  let { x, y, width, height } = frame;

  if (handle.includes("w")) {
    x = clamp({
      value: x,
      min: content.x,
      max: originRight - minSize,
    });
    width = originRight - x;
  } else if (handle.includes("e")) {
    width = clamp({
      value: width,
      min: minSize,
      max: Math.max(minSize, contentRight - originFrame.x),
    });
    x = originFrame.x;
  }

  if (handle.includes("n")) {
    y = clamp({
      value: y,
      min: content.y,
      max: originBottom - minSize,
    });
    height = originBottom - y;
  } else if (handle.includes("s")) {
    height = clamp({
      value: height,
      min: minSize,
      max: Math.max(minSize, contentBottom - originFrame.y),
    });
    y = originFrame.y;
  }

  return { x, y, width, height };
};

export const cropPixelBoxFromHandle = ({
  originFrame,
  originCrop,
  corner,
  startPoint,
  point,
  minSize = 8,
}) => {
  const handle = CANONICAL_CORNER[corner] || corner;
  const content = contentBoxFromFrame({
    box: originFrame,
    crop: originCrop,
  });
  const nextFrame = resizeFrameIndependent({
    origin: originFrame,
    handle,
    startPoint,
    point,
    minSize,
  });
  const clamped = clampFrameToContent({
    frame: nextFrame,
    content,
    originFrame,
    handle,
    minSize,
  });
  const crop = cropFromFrameInContent({ frame: clamped, content }) ?? null;
  return {
    ...clamped,
    crop,
  };
};

export const croppedImageSx = ({ crop } = {}) => {
  const frameSx = { left: 0, top: 0, width: "100%", height: "100%" };
  if (!isIdentityCrop(crop)) {
    const next = normalizeCrop(crop);
    frameSx.width = `${100 / next.width}%`;
    frameSx.height = `${100 / next.height}%`;
    frameSx.left = `${(-next.x / next.width) * 100}%`;
    frameSx.top = `${(-next.y / next.height) * 100}%`;
  }
  return {
    position: "absolute",
    right: "auto",
    bottom: "auto",
    ...frameSx,
    maxWidth: "none",
    maxHeight: "none",
  };
};

export const isCropHandleGesture = ({
  event,
  shiftHeld,
  layer,
  selectedCount = 1,
} = {}) =>
  Boolean(
    (event?.shiftKey || shiftHeld) &&
      selectedCount === 1 &&
      canCropImageLayer(layer)
  );
